import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  featuredCarousel;
  #featuredCarouselSetupFrame = 0;
  #featuredCarouselObserver = new MutationObserver(() => {
    this.#queueFeaturedCarouselSetup();
  });

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#featuredCarouselObserver.observe(this, {
      childList: true,
      subtree: true,
    });
    this.#queueFeaturedCarouselSetup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#featuredCarouselObserver.disconnect();
    if (this.#featuredCarouselSetupFrame) cancelAnimationFrame(this.#featuredCarouselSetupFrame);
    this.featuredCarousel?.destroy?.();
    this.featuredCarousel = undefined;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#queueFeaturedCarouselSetup();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.#setupFeaturedCarousel();
      this.featuredCarousel?.reset();

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  #queueFeaturedCarouselSetup() {
    if (this.#featuredCarouselSetupFrame) return;

    this.#featuredCarouselSetupFrame = requestAnimationFrame(() => {
      this.#featuredCarouselSetupFrame = 0;
      this.#setupFeaturedCarousel();
    });
  }

  #setupFeaturedCarousel() {
    const carousel = this.querySelector('[data-featured-carousel]');
    const track = carousel?.querySelector('[data-featured-track]');
    const slides = track ? Array.from(track.querySelectorAll('[data-featured-slide]')) : [];
    const previousButton = carousel?.querySelector('[data-featured-prev]');
    const nextButton = carousel?.querySelector('[data-featured-next]');
    const isValidCarousel =
      !!carousel &&
      track instanceof HTMLElement &&
      slides.length > 0 &&
      previousButton instanceof HTMLButtonElement &&
      nextButton instanceof HTMLButtonElement;

    if (!isValidCarousel) {
      this.featuredCarousel?.destroy?.();
      this.featuredCarousel = undefined;
      return;
    }

    const hasSameBinding =
      this.featuredCarousel?.carousel === carousel &&
      this.featuredCarousel?.track === track &&
      this.featuredCarousel?.previousButton === previousButton &&
      this.featuredCarousel?.nextButton === nextButton &&
      this.featuredCarousel?.slides?.length === slides.length &&
      this.featuredCarousel.slides.every((slide, index) => slide === slides[index]);

    if (hasSameBinding) {
      this.featuredCarousel.update?.();
      return;
    }

    this.featuredCarousel?.destroy?.();

    let frame = 0;
    let updateTimeout = 0;
    track.style.transform = '';
    const scrollTolerance = 10;

    const getSlidePositions = () => {
      const firstOffset = slides[0]?.offsetLeft ?? 0;
      return slides.map((slide) => slide.offsetLeft - firstOffset);
    };

    const getNearestIndex = () => {
      const positions = getSlidePositions();
      const currentLeft = track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      positions.forEach((position, index) => {
        const distance = Math.abs(position - currentLeft);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    };

    const updateButtons = () => {
      const atStart = track.scrollLeft <= scrollTolerance;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - scrollTolerance;

      previousButton.disabled = slides.length < 2 || atStart;
      previousButton.hidden = slides.length < 2 || atStart;
      nextButton.disabled = slides.length < 2 || atEnd;
      nextButton.hidden = slides.length < 2 || atEnd;
    };

    const scheduleButtonUpdate = () => {
      if (frame) cancelAnimationFrame(frame);
      if (updateTimeout) clearTimeout(updateTimeout);

      frame = requestAnimationFrame(() => {
        frame = 0;
        updateButtons();
      });

      updateTimeout = window.setTimeout(() => {
        updateTimeout = 0;
        updateButtons();
      }, 350);
    };

    const moveCarousel = (direction) => {
      const positions = getSlidePositions();
      if (positions.length < 2) return;
      const currentIndex = getNearestIndex();
      const targetIndex = Math.max(0, Math.min(currentIndex + direction, positions.length - 1));

      if (targetIndex === currentIndex) {
        scheduleButtonUpdate();
        return;
      }

      track.scrollTo({
        left: positions[targetIndex],
        behavior: 'smooth',
      });

      scheduleButtonUpdate();
    };

    const onPreviousClick = () => {
      moveCarousel(-1);
    };

    const onNextClick = () => {
      moveCarousel(1);
    };

    previousButton.addEventListener('click', onPreviousClick);
    nextButton.addEventListener('click', onNextClick);

    track.addEventListener('scroll', scheduleButtonUpdate, { passive: true });

    const onResize = () => {
      scheduleButtonUpdate();
    };

    window.addEventListener('resize', onResize);

    this.featuredCarousel = {
      carousel,
      track,
      previousButton,
      nextButton,
      slides,
      update: updateButtons,
      reset: () => {
        track.scrollLeft = 0;
        updateButtons();
        requestAnimationFrame(updateButtons);
      },
      destroy: () => {
        if (frame) cancelAnimationFrame(frame);
        if (updateTimeout) clearTimeout(updateTimeout);
        previousButton.removeEventListener('click', onPreviousClick);
        nextButton.removeEventListener('click', onNextClick);
        track.removeEventListener('scroll', scheduleButtonUpdate);
        window.removeEventListener('resize', onResize);
      },
    };

    requestAnimationFrame(() => {
      this.featuredCarousel?.reset();
      scheduleButtonUpdate();
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
